import React, { useState, useEffect } from 'react';
import { getAllowlist, addEmailToAllowlist, removeEmailFromAllowlist } from '../services/allowlist';
import { getClaimedPlayers, unclaimPlayer } from '../services/claimedPlayers';
import { AllowlistEntry, ClaimedPlayer } from '../generated-api';
import { UserPlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import PageHeader from '../components/common/PageHeader';

const AllowlistPage: React.FC = () => {
  const [allowlist, setAllowlist] = useState<AllowlistEntry[]>([]);
  const [claimedPlayers, setClaimedPlayers] = useState<ClaimedPlayer[]>([]);
  const [newEmail, setNewEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [claimedPlayersLoading, setClaimedPlayersLoading] = useState(true);

  useEffect(() => {
    fetchAllowlist();
    fetchClaimedPlayers();
  }, []);

  const fetchAllowlist = async () => {
    setLoading(true);
    try {
      const data = await getAllowlist();
      setAllowlist(data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch allowlist');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail) return;
    try {
      await addEmailToAllowlist(newEmail);
      setNewEmail('');
      setError(null);
      fetchAllowlist();
    } catch (err) {
      setError('Failed to add email. It may already be in the list.');
    }
  };

  const handleRemoveEmail = async (email: string) => {
    try {
      await removeEmailFromAllowlist(email);
      setError(null);
      fetchAllowlist();
    } catch (err) {
      setError('Failed to remove email');
    }
  };

  const fetchClaimedPlayers = async () => {
    setClaimedPlayersLoading(true);
    try {
      const data = await getClaimedPlayers();
      setClaimedPlayers(data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch claimed players');
    } finally {
      setClaimedPlayersLoading(false);
    }
  };

  const handleUnclaimPlayer = async (playerId: string) => {
    try {
      await unclaimPlayer(playerId);
      setError(null);
      fetchClaimedPlayers();
    } catch (err) {
      setError('Failed to unclaim player');
    }
  };

  if (loading || claimedPlayersLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader className="mb-8">
        <h1 className="text-fluid-h1 font-bold text-heading">Email Allowlist Management</h1>
        <p className="mt-2 text-body">
          Add or remove emails from the login allowlist. Only users with emails in this list can register.
        </p>
      </PageHeader>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
          <span className="block sm:inline">{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-1 gap-6 mb-8">
        <div className="bg-surface overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <h3 className="text-lg font-medium text-heading mb-4">Add New Email</h3>
            <form onSubmit={handleAddEmail} className="flex items-center space-x-4">
              <div className="flex-grow">
                <label htmlFor="email" className="sr-only">Email</label>
                <input
                  id="email"
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="Enter email to add"
                  className="w-full px-4 py-2 border border-edge-input rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                  required
                />
              </div>
              <button
                type="submit"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <UserPlusIcon className="h-5 w-5 mr-2" />
                Add Email
              </button>
            </form>
          </div>
        </div>
      </div>

      <div className="bg-surface shadow rounded-lg">
        <div className="px-6 py-4 border-b border-edge">
          <h3 className="text-lg font-medium text-heading">Allowed Emails</h3>
        </div>
        <div className="p-0">
          <ul className="divide-y divide-edge">
            {allowlist.length > 0 ? (
              allowlist.map((entry) => (
                <li key={entry.email} className="px-6 py-4 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-heading">{entry.email}</p>
                    <p className="text-sm text-muted">
                      Added by {entry.addedBy} on {new Date(entry.addedAt).toLocaleString()}
                    </p>
                  </div>
                  <button
                    data-track="allowlist_remove"
                    onClick={() => handleRemoveEmail(entry.email)}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    <TrashIcon className="h-4 w-4 mr-1.5" />
                    Remove
                  </button>
                </li>
              ))
            ) : (
              <li className="px-6 py-4 text-center text-muted">
                No emails in the allowlist.
              </li>
            )}
          </ul>
        </div>
      </div>

      <div className="bg-surface shadow rounded-lg mt-8">
        <div className="px-6 py-4 border-b border-edge">
          <h3 className="text-lg font-medium text-heading">Claimed Players</h3>
        </div>
        <div className="p-0">
          <ul className="divide-y divide-edge">
            {claimedPlayers.length > 0 ? (
              claimedPlayers.map((player) => (
                <li key={player.id} className="px-6 py-4 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-heading">{player.name}</p>
                    <p className="text-sm text-muted">{player.email}</p>
                  </div>
                  <button
                    data-track="allowlist_unclaim"
                    onClick={() => handleUnclaimPlayer(player.id)}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    <TrashIcon className="h-4 w-4 mr-1.5" />
                    Unclaim
                  </button>
                </li>
              ))
            ) : (
              <li className="px-6 py-4 text-center text-muted">
                No claimed players.
              </li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AllowlistPage;